import React, {useState, useEffect, useRef, useMemo} from "react";
import {useTranslation} from "react-i18next";
import cn from "classnames";
//local
import {
  IconHandOnDown,
  IconHandOnUp,
  IconHandOnRight,
  IconHandOnLeft,
} from "components/icons";

const useInView = (ref) => {
  const [isInView, setIsInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setIsInView(true);
        observer.disconnect();
      },
      {threshold: 0.1}
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);
  return isInView;
};

const useMatchMediaHide = (maxWidth) => {
  const [isHidden, setIsHidden] = useState(false);
  useEffect(() => {
    if (!maxWidth) return;
    const mq = window.matchMedia(`(max-width: ${maxWidth - 1}px)`);
    const handler = (e) => setIsHidden(e.matches);
    setIsHidden(mq.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [maxWidth]);
  return isHidden;
};

const useFadeOut = (isInView, delay) => {
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [isGone, setIsGone] = useState(false);
  useEffect(() => {
    if (!isInView || !delay) return;
    const timer = setTimeout(() => setIsFadingOut(true), delay);
    return () => clearTimeout(timer);
  }, [isInView, delay]);
  return {isFadingOut, onFadeEnd: () => isFadingOut && setIsGone(true), isGone};
};

const HandyPointerBlob = ({
  text, // Translation key for text
  showNewBadge, // Show or hide red "new" badge
  showNewsBadge, // Show or hide red "news" badge
  align = "center", // Flex alignment of text and pointer
  absolutePosition = false, // Force absolute positioning
  position = {}, // pixel values for positioning
  showPointer, // Show or hide pointy hand icon
  pointerPosition = "", // ['first', 'last'] pointer position regarding the content
  pointerDirection = "down", // ['down', 'up', 'left', 'right']
  pointerAnimation, // depends on pointerDirection
  noPointerAnimation, // Include to stop wiggly animation
  addClass, // Wrapper class
  hideOnScreenWidthUnder, // Hide component below this pixel width
  fadeOutAfter, // ms delay after entering view before fade-out kicks in
}) => {
  const {t} = useTranslation();
  const wrapperRef = useRef(null);

  const isInView = useInView(wrapperRef);
  const isHidden = useMatchMediaHide(hideOnScreenWidthUnder);
  const {isFadingOut, onFadeEnd, isGone} = useFadeOut(isInView, fadeOutAfter);

  const animationClass = useMemo(() => {
    if (!isInView || noPointerAnimation) return "";
    if (pointerAnimation)
      return ["left", "right"].includes(pointerDirection)
        ? "pointer-animation-horizontal"
        : "pointer-animation-vertical";
    return "wiggly-animation";
  }, [isInView, noPointerAnimation, pointerAnimation, pointerDirection]);

  const iconClass = cn(animationClass, `show-${pointerPosition}`);

  const styleValues = useMemo(
    () => ({
      position: absolutePosition ? "absolute" : "relative",
      ...position,
      display: isHidden || isGone ? "none" : undefined,
    }),
    [absolutePosition, position, isHidden, isGone]
  );

  return (
    <div
      ref={wrapperRef}
      className={cn("handy-pointer-blob", `align-${align}`, {
        "handy-pointer-blob-fading-out": isFadingOut,
        [addClass]: !!addClass,
      })}
      style={styleValues}
      onAnimationEnd={onFadeEnd}
    >
      {showNewBadge && <div className="newbadge">{t("NEW_FEATURE_NEW")}</div>}
      {showNewsBadge && <div className="newsbadge">{t("NEW_FEATURE_NEWS")}</div>}
      {showPointer && (
        <React.Fragment>
          {pointerDirection === "down" && <IconHandOnDown addClass={iconClass} />}
          {pointerDirection === "up" && <IconHandOnUp addClass={iconClass} />}
          {pointerDirection === "left" && <IconHandOnLeft addClass={iconClass} />}
          {pointerDirection === "right" && <IconHandOnRight addClass={iconClass} />}
        </React.Fragment>
      )}
      <div className="scribble">{t(text)}</div>
    </div>
  );
};

export default HandyPointerBlob;
